import {FC,useMemo, ReactNode} from 'react';
import {
  Project,
  Team,
} from "@brg/api";
import {
  FlexBox,
  useIsMobile,
  useBreakpoint,
  NavbarButtonFilled
} from "@brg/ui";
import { useProfile } from "core/auth";
import styled, { ThemeProvider } from "styled-components";

import ApprovalDialog from "../components/ApprovalDialog";
import ProjectButtons from "../components/ProjectButtons";
import { LayoutConst } from 'core';
import { useGetCurrentTeam } from "../../../hooks/useGetCurrentTeam";
import OrganizationApprovalDialog from "../components/OrganizationApprovalDialog";
import { IPollInfo } from "../../../constants/IPollInfo";
import { ProjectStartGameButton } from "../components/ProjectStartGameButton";
import { SP_VOTE_COLLECTION_SIGN } from "../../VotesAdmin/VotesAdminPage";
import { useHistory, useRouteMatch } from 'react-router-dom';
import { CreateLinkTo, LinkTo } from 'shared/constants/linkTo';
import { AppTester } from 'supporting';
import VoteBlock from '../components/VoteBlock/VoteBlock';
import { useTranslation } from 'react-i18next';
import { AutoTranslateStatus } from 'shared/pages/projectEdit/types';
import { useTheme } from 'styled-components';
import { bProfile } from '@brg/auth';

export enum VOTE_BLOCK_POSITION {
  top = 'top',
  longread = 'longread',
  bottom = 'bottom'
}

const BlocksWrapper = styled(FlexBox)<{ iSBookMainPage?: boolean, isCollectionVote?: boolean }>`
  width: 100%;
  flex-direction: column;
  row-gap: ${({ isCollectionVote }) => isCollectionVote ? '0px' : '24px'};
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    row-gap: ${({ isCollectionVote }) => isCollectionVote ? '0px' : '16px'};
  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    row-gap: ${({ isCollectionVote }) => isCollectionVote ? '0px' : '8px'};
  }
  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    row-gap: 0px;
    width: ${({ iSBookMainPage }) => iSBookMainPage ? '100%' : 'calc(100% - 20px)'};
    margin: ${({ iSBookMainPage }) => iSBookMainPage ? '0 auto' : '0 10px'};
  }
`
const TranslatedBlock = styled(FlexBox)`
  min-width: 500px;
  padding: 12px 24px;
  background: #F2F2F2;
  justify-content: space-between;
  border-radius: 3px;
  font-size: 15px;
  line-height: 19px;
  color: #545453;
  @media (max-width: 1440px), (max-height: 920px) {
    width: calc(100% - 24px);
    min-width: auto;
}
   @media (max-width: ${LayoutConst.screen.md}px) {
    min-width: 300px;
    margin-left: 16px;
    padding: 8px 15px;
  }
   @media (max-width: ${LayoutConst.screen.s}px) {
    width: calc(100% - 20px);
    margin: 16px auto;
    flex-direction: column;
  }
`
const TranslatedButton = styled(NavbarButtonFilled)`
  min-width: 175px;
  padding: '8px 24px';
   @media (max-width: ${LayoutConst.screen.md}px) {
    min-width: 140px;
    padding: '8px 15px';
  }
   @media (max-width: ${LayoutConst.screen.s}px) {
    min-width: 100%;
    padding: '8px 15px';
    margin-top: 16px;
  }
`
// export interface Criteria {
//   voteCriteriaId: string;
//   name: string | LangObject[];
//   nominationId?: string;
//   minValue: number;
//   maxValue: number;
//   weight?: number;
//   currentLang?: string;
//   isNew?: boolean;
// }

type PropsT = {
  project: any;
  channel: Team;
  onShouldReload?: (project: Project) => void;
  reloadProject?: (time: any) => void;
  isProjectPage?: boolean;
  isEmptyProjectTags?: boolean;
  orientation?: 'horizontal' | 'vertical';
  isPresentationTooltip?: boolean;
  isPresentation?: boolean;
  iSBookMainPage?: boolean;
  isOutsideTextPosition?: boolean;
  pollInfo?: IPollInfo;
  queueButton?: ReactNode | null;
  isOwner?: boolean;
  onBlockedStateChange?: (isBlocked: boolean, reasons: number[], otherReason: string) => void;
};

export const PSBottom: FC<PropsT> = ({
  project,
  channel,
  onShouldReload,
  reloadProject,
  isProjectPage,
  iSBookMainPage,
  pollInfo,
  isEmptyProjectTags,
  orientation,
  isPresentationTooltip,
  isPresentation,
 queueButton,
  isOutsideTextPosition,
  isOwner,
  onBlockedStateChange
}) => {
  const isMobile = useIsMobile();
  const is744 = useBreakpoint(LayoutConst.screen.s);
  const is743 = useBreakpoint(LayoutConst.screen.s - 1);
  const theme = useTheme();
  const currentTeam = useGetCurrentTeam();
  const is944 = useBreakpoint(940);
  const isCollectionVote = useMemo(() => (project?.contexts?.includes(SP_VOTE_COLLECTION_SIGN)), [project])
  const profile = useProfile();
  const isPreview = !!useRouteMatch(LinkTo.preview);
  const isSuperPuper = AppTester.isSuperPuper();
  const isMediiia = AppTester.isMediiia();
  const history = useHistory();
  const isModer = useMemo(() => profile && bProfile.isModer(profile), [profile]);
  const { t } = useTranslation();

  const viewType = project?.longread?.viewType || 'container';

  const showApprovalDialog = !['t-shape', 'fixed-ratio-large', 'fixed-ratio-small'].includes(viewType);
  
  const translatedBlock = isOwner && (<TranslatedBlock align='center' offsetLeft={24} offsetTop={24}>
    <div>
      <span>{t('project_edit.automatically_translated')}</span>
      <span style={{marginLeft: '4px'}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 9 9" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M5.2241e-08 5.20356C5.2241e-08 5.40902 0.132188 5.59169 0.327656 5.65525L2.39344 6.32659L3.06422 8.39139C3.09525 8.48666 3.15563 8.56967 3.23671 8.62854C3.3178 8.6874 3.41543 8.7191 3.51562 8.7191C3.61582 8.7191 3.71345 8.6874 3.79454 8.62854C3.87562 8.56967 3.936 8.48666 3.96703 8.39139L4.63922 6.32659L6.70359 5.65525C6.79887 5.62418 6.88187 5.56377 6.94072 5.48266C6.99957 5.40156 7.03126 5.30391 7.03125 5.2037C7.03125 4.99811 6.89906 4.81572 6.70359 4.75216L4.63922 4.08081L3.96703 2.01602C3.93607 1.92067 3.87572 1.83758 3.79463 1.77866C3.71354 1.71973 3.61587 1.68799 3.51562 1.68799C3.41538 1.68799 3.31771 1.71973 3.23662 1.77866C3.15553 1.83758 3.09518 1.92067 3.06422 2.01602L2.39344 4.08081L0.327656 4.75216C0.232373 4.78316 0.149351 4.84354 0.0904903 4.92463C0.0316299 5.00572 -4.69545e-05 5.10336 5.2241e-08 5.20356Z" fill="#909090"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M7.15781 0.194686C7.13961 0.138136 7.10394 0.0888189 7.05592 0.0538339C7.00791 0.018849 6.95003 0 6.89062 0C6.83122 0 6.77334 0.018849 6.72533 0.0538339C6.67731 0.0888189 6.64164 0.138136 6.62344 0.194686L6.21844 1.43795L4.97531 1.84225C4.86 1.87994 4.78125 1.98794 4.78125 2.10972C4.78125 2.2315 4.86 2.3395 4.97531 2.37719L6.21844 2.78148L6.62344 4.02475C6.64164 4.0813 6.67731 4.13061 6.72533 4.1656C6.77334 4.20058 6.83122 4.21943 6.89062 4.21943C6.95003 4.21943 7.00791 4.20058 7.05592 4.1656C7.10394 4.13061 7.13961 4.0813 7.15781 4.02475L7.56281 2.78148L8.80594 2.37719C8.92125 2.3395 9 2.2315 9 2.10972C9 1.98794 8.92125 1.87994 8.80594 1.84225L7.56281 1.43795L7.15781 0.194686Z" fill="#909090"/>
            </svg>
      </span>
    </div>
    <TranslatedButton onClick={ () => history.push(CreateLinkTo.projectEdit(project.projectId))}>{t('project_edit.translate_edit_button')}</TranslatedButton>
  </TranslatedBlock>)
  if (!project) {
    return null;
  }



  if (isProjectPage && is944 && !is743) {
    return (
      <>
        <FlexBox align="flex-end" alignMobile='flex-end' directionMobile={isPresentationTooltip ? 'column' : 'row'}>
          <ProjectButtons isPresentation={isPresentation} isOutsideTextPosition={isOutsideTextPosition}
                          dropdownVerticalPos={isEmptyProjectTags ? 'top' : "bottom"} project={project}
                          orientation={orientation} dropdownPos={isPresentationTooltip ? "right" : "left"}
                          onShouldReload={onShouldReload} isProjectPage={isProjectPage}
                          isPresentationTooltip={isPresentationTooltip}/>
          <FlexBox width={'100%'} widthMobile={'100%'} direction={"column"} directionMobile={"column"}>
            <ThemeProvider theme={theme as any}>
              <BlocksWrapper>
                {isCollectionVote && !isPreview && isModer && isSuperPuper &&
                <FlexBox width={'100%'} justify="flex-end"><FlexBox width={is744 ? '100%' : undefined}
                                                                    offsetRightMobile={is744 ? 0 : 16}><ProjectStartGameButton
                  replayOnly projectId={project?.projectId} pollInfo={pollInfo}/></FlexBox></FlexBox>}
                {!isSuperPuper && <FlexBox width={'100%'} justify="flex-end">{queueButton}</FlexBox>}
                {!isCollectionVote && showApprovalDialog && (currentTeam ?
                  <OrganizationApprovalDialog project={project} onShouldReload={onShouldReload}/> :
                  <ApprovalDialog reloadProject={reloadProject} project={project} onShouldReload={onShouldReload}
                                  isProjectPage/>)}
              </BlocksWrapper>
              {project.autoTranslateStatus === AutoTranslateStatus.Complete && translatedBlock}
            </ThemeProvider>
          </FlexBox>
        </FlexBox>
      </>
    )
  }

  return (
    <>
      <FlexBox align="flex-end" alignMobile='flex-start' width={'100%'} className={'ps-bottom-wrap'}>
        {!is944 && <ProjectButtons isPresentation={isPresentation} isOutsideTextPosition={isOutsideTextPosition}
                                   dropdownVerticalPos={isEmptyProjectTags ? 'top' : "bottom"} orientation={orientation}
                                   dropdownPos={isPresentationTooltip ? "right" : "left"} project={project}
                                   onShouldReload={onShouldReload} isPresentationTooltip={isPresentationTooltip}/>}
        <FlexBox className='vote-approval-block' width={'100%'} widthMobile={'100%'} direction={"column"} directionMobile={"column"}>
          <ThemeProvider theme={theme as any}>
            <BlocksWrapper iSBookMainPage={iSBookMainPage} isCollectionVote={isCollectionVote}>
              {
                <FlexBox width='100%' offsetLeft={!isMobile ? 24 : 0}>
                  <VoteBlock project={project} position={VOTE_BLOCK_POSITION.top}/>
                </FlexBox>
              }
              {(!isMobile || is744) && isModer && !isPreview && (isSuperPuper || isMediiia) &&
              <FlexBox 
                className='start-game-btn-wrapper' 
                width={'100%'} 
                justify="flex-end">
                  <FlexBox width={is744 ? '100%' : undefined} offsetRightMobile={is744 ? 0 : 16}>
                    <ProjectStartGameButton
                      replayOnly 
                      projectId={project?.projectId} 
                      pollInfo={pollInfo}
                    />
                  </FlexBox>
                </FlexBox>}
              {(!isMobile || is744) && !isSuperPuper && <FlexBox className='queue-btn-wrapper' offsetTop={is744 ? 16 : 0} width={'100%'} justify="flex-end">
                {queueButton}
              </FlexBox>}
              {/* у организаций будет отдельная плашка модерации*/}
              {(!isMobile || is744) && !isCollectionVote && showApprovalDialog && (currentTeam ?
                <OrganizationApprovalDialog project={project} onShouldReload={onShouldReload}/> :
                <ApprovalDialog project={project} reloadProject={reloadProject} onShouldReload={onShouldReload} onBlockedStateChange={onBlockedStateChange}/>)}
            </BlocksWrapper>
            {project.autoTranslateStatus === AutoTranslateStatus.Complete && translatedBlock}
          </ThemeProvider>
        </FlexBox>
      </FlexBox>
    </>
  );
};
import {FC,useMemo, ReactNode} from 'react';
import {
  Project,
  Team,
} from "@brg/api";
import {
  FlexBox,
  useIsMobile,
  useBreakpoint,
  NavbarButtonFilled
} from "@brg/ui";
import { useProfile } from "core/auth";
import styled, { ThemeProvider } from "styled-components";

import ApprovalDialog from "../components/ApprovalDialog";
import ProjectButtons from "../components/ProjectButtons";
import { LayoutConst } from 'core';
import { useGetCurrentTeam } from "../../../hooks/useGetCurrentTeam";
import OrganizationApprovalDialog from "../components/OrganizationApprovalDialog";
import { IPollInfo } from "../../../constants/IPollInfo";
import { ProjectStartGameButton } from "../components/ProjectStartGameButton";
import { SP_VOTE_COLLECTION_SIGN } from "../../VotesAdmin/VotesAdminPage";
import { useHistory, useRouteMatch } from 'react-router-dom';
import { CreateLinkTo, LinkTo } from 'shared/constants/linkTo';
import { AppTester } from 'supporting';
import VoteBlock from '../components/VoteBlock/VoteBlock';
import { useTranslation } from 'react-i18next';
import { AutoTranslateStatus } from 'shared/pages/projectEdit/types';
import { useTheme } from 'styled-components';
import { bProfile } from '@brg/auth';

export enum VOTE_BLOCK_POSITION {
  top = 'top',
  longread = 'longread',
  bottom = 'bottom'
}

const BlocksWrapper = styled(FlexBox)<{ iSBookMainPage?: boolean, isCollectionVote?: boolean }>`
  width: 100%;
  flex-direction: column;
  row-gap: ${({ isCollectionVote }) => isCollectionVote ? '0px' : '24px'};
  box-sizing: border-box;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    row-gap: ${({ isCollectionVote }) => isCollectionVote ? '0px' : '16px'};
  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    row-gap: ${({ isCollectionVote }) => isCollectionVote ? '0px' : '8px'};
  }
  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    row-gap: 0px;
    width: ${({ iSBookMainPage }) => iSBookMainPage ? '100%' : 'calc(100% - 20px)'};
    margin: ${({ iSBookMainPage }) => iSBookMainPage ? '0 auto' : '0 10px'};
  }
`
const TranslatedBlock = styled(FlexBox)`
  min-width: 500px;
  padding: 12px 24px;
  background: #F2F2F2;
  justify-content: space-between;
  border-radius: 3px;
  font-size: 15px;
  line-height: 19px;
  color: #545453;
  @media (max-width: 1440px), (max-height: 920px) {
    width: calc(100% - 24px);
    min-width: auto;
}
   @media (max-width: ${LayoutConst.screen.md}px) {
    min-width: 300px;
    margin-left: 16px;
    padding: 8px 15px;
  }
   @media (max-width: ${LayoutConst.screen.s}px) {
    width: calc(100% - 20px);
    margin: 16px auto;
    flex-direction: column;
  }
`
const TranslatedButton = styled(NavbarButtonFilled)`
  min-width: 175px;
  padding: '8px 24px';
   @media (max-width: ${LayoutConst.screen.md}px) {
    min-width: 140px;
    padding: '8px 15px';
  }
   @media (max-width: ${LayoutConst.screen.s}px) {
    min-width: 100%;
    padding: '8px 15px';
    margin-top: 16px;
  }
`
// export interface Criteria {
//   voteCriteriaId: string;
//   name: string | LangObject[];
//   nominationId?: string;
//   minValue: number;
//   maxValue: number;
//   weight?: number;
//   currentLang?: string;
//   isNew?: boolean;
// }

type PropsT = {
  project: any;
  channel: Team;
  onShouldReload?: (project: Project) => void;
  reloadProject?: (time: any) => void;
  isProjectPage?: boolean;
  isEmptyProjectTags?: boolean;
  orientation?: 'horizontal' | 'vertical';
  isPresentationTooltip?: boolean;
  isPresentation?: boolean;
  iSBookMainPage?: boolean;
  isOutsideTextPosition?: boolean;
  pollInfo?: IPollInfo;
  queueButton?: ReactNode | null;
  isOwner?: boolean;
  onBlockedStateChange?: (isBlocked: boolean, reasons: number[], otherReason: string) => void;
};

export const PSBottom: FC<PropsT> = ({
  project,
  channel,
  onShouldReload,
  reloadProject,
  isProjectPage,
  iSBookMainPage,
  pollInfo,
  isEmptyProjectTags,
  orientation,
  isPresentationTooltip,
  isPresentation,
 queueButton,
  isOutsideTextPosition,
  isOwner,
  onBlockedStateChange
}) => {
  const isMobile = useIsMobile();
  const is744 = useBreakpoint(LayoutConst.screen.s);
  const is743 = useBreakpoint(LayoutConst.screen.s - 1);
  const theme = useTheme();
  const currentTeam = useGetCurrentTeam();
  const is944 = useBreakpoint(940);
  const isCollectionVote = useMemo(() => (project?.contexts?.includes(SP_VOTE_COLLECTION_SIGN)), [project])
  const profile = useProfile();
  const isPreview = !!useRouteMatch(LinkTo.preview);
  const isSuperPuper = AppTester.isSuperPuper();
  const isMediiia = AppTester.isMediiia();
  const history = useHistory();
  const isModer = useMemo(() => profile && bProfile.isModer(profile), [profile]);
  const { t } = useTranslation();

  const viewType = project?.longread?.viewType || 'container';

  const showApprovalDialog = !['t-shape', 'fixed-ratio-large', 'fixed-ratio-small'].includes(viewType);
  
  const translatedBlock = isOwner && (<TranslatedBlock align='center' offsetLeft={24} offsetTop={24}>
    <div>
      <span>{t('project_edit.automatically_translated')}</span>
      <span style={{marginLeft: '4px'}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 9 9" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M5.2241e-08 5.20356C5.2241e-08 5.40902 0.132188 5.59169 0.327656 5.65525L2.39344 6.32659L3.06422 8.39139C3.09525 8.48666 3.15563 8.56967 3.23671 8.62854C3.3178 8.6874 3.41543 8.7191 3.51562 8.7191C3.61582 8.7191 3.71345 8.6874 3.79454 8.62854C3.87562 8.56967 3.936 8.48666 3.96703 8.39139L4.63922 6.32659L6.70359 5.65525C6.79887 5.62418 6.88187 5.56377 6.94072 5.48266C6.99957 5.40156 7.03126 5.30391 7.03125 5.2037C7.03125 4.99811 6.89906 4.81572 6.70359 4.75216L4.63922 4.08081L3.96703 2.01602C3.93607 1.92067 3.87572 1.83758 3.79463 1.77866C3.71354 1.71973 3.61587 1.68799 3.51562 1.68799C3.41538 1.68799 3.31771 1.71973 3.23662 1.77866C3.15553 1.83758 3.09518 1.92067 3.06422 2.01602L2.39344 4.08081L0.327656 4.75216C0.232373 4.78316 0.149351 4.84354 0.0904903 4.92463C0.0316299 5.00572 -4.69545e-05 5.10336 5.2241e-08 5.20356Z" fill="#909090"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M7.15781 0.194686C7.13961 0.138136 7.10394 0.0888189 7.05592 0.0538339C7.00791 0.018849 6.95003 0 6.89062 0C6.83122 0 6.77334 0.018849 6.72533 0.0538339C6.67731 0.0888189 6.64164 0.138136 6.62344 0.194686L6.21844 1.43795L4.97531 1.84225C4.86 1.87994 4.78125 1.98794 4.78125 2.10972C4.78125 2.2315 4.86 2.3395 4.97531 2.37719L6.21844 2.78148L6.62344 4.02475C6.64164 4.0813 6.67731 4.13061 6.72533 4.1656C6.77334 4.20058 6.83122 4.21943 6.89062 4.21943C6.95003 4.21943 7.00791 4.20058 7.05592 4.1656C7.10394 4.13061 7.13961 4.0813 7.15781 4.02475L7.56281 2.78148L8.80594 2.37719C8.92125 2.3395 9 2.2315 9 2.10972C9 1.98794 8.92125 1.87994 8.80594 1.84225L7.56281 1.43795L7.15781 0.194686Z" fill="#909090"/>
            </svg>
      </span>
    </div>
    <TranslatedButton onClick={ () => history.push(CreateLinkTo.projectEdit(project.projectId))}>{t('project_edit.translate_edit_button')}</TranslatedButton>
  </TranslatedBlock>)
  if (!project) {
    return null;
  }



  if (isProjectPage && is944 && !is743) {
    return (
      <>
        <FlexBox align="flex-end" alignMobile='flex-end' directionMobile={isPresentationTooltip ? 'column' : 'row'}>
          <ProjectButtons isPresentation={isPresentation} isOutsideTextPosition={isOutsideTextPosition}
                          dropdownVerticalPos={isEmptyProjectTags ? 'top' : "bottom"} project={project}
                          orientation={orientation} dropdownPos={isPresentationTooltip ? "right" : "left"}
                          onShouldReload={onShouldReload} isProjectPage={isProjectPage}
                          isPresentationTooltip={isPresentationTooltip}/>
          <FlexBox width={'100%'} widthMobile={'100%'} direction={"column"} directionMobile={"column"}>
            <ThemeProvider theme={theme as any}>
              <BlocksWrapper>
                {isCollectionVote && !isPreview && isModer && isSuperPuper &&
                <FlexBox width={'100%'} justify="flex-end"><FlexBox width={is744 ? '100%' : undefined}
                                                                    offsetRightMobile={is744 ? 0 : 16}><ProjectStartGameButton
                  replayOnly projectId={project?.projectId} pollInfo={pollInfo}/></FlexBox></FlexBox>}
                {!isSuperPuper && <FlexBox width={'100%'} justify="flex-end">{queueButton}</FlexBox>}
                {!isCollectionVote && showApprovalDialog && (currentTeam ?
                  <OrganizationApprovalDialog project={project} onShouldReload={onShouldReload}/> :
                  <ApprovalDialog reloadProject={reloadProject} project={project} onShouldReload={onShouldReload}
                                  isProjectPage/>)}
              </BlocksWrapper>
              {project.autoTranslateStatus === AutoTranslateStatus.Complete && translatedBlock}
            </ThemeProvider>
          </FlexBox>
        </FlexBox>
      </>
    )
  }

  return (
    <>
      <FlexBox align="flex-end" alignMobile='flex-start' width={'100%'} className={'ps-bottom-wrap'}>
        {!is944 && <ProjectButtons isPresentation={isPresentation} isOutsideTextPosition={isOutsideTextPosition}
                                   dropdownVerticalPos={isEmptyProjectTags ? 'top' : "bottom"} orientation={orientation}
                                   dropdownPos={isPresentationTooltip ? "right" : "left"} project={project}
                                   onShouldReload={onShouldReload} isPresentationTooltip={isPresentationTooltip}/>}
        <FlexBox className='vote-approval-block' width={'100%'} widthMobile={'100%'} direction={"column"} directionMobile={"column"}>
          <ThemeProvider theme={theme as any}>
            <BlocksWrapper iSBookMainPage={iSBookMainPage} isCollectionVote={isCollectionVote}>
              {
                <FlexBox width='100%' offsetLeft={!isMobile ? 24 : 0}>
                  <VoteBlock project={project} position={VOTE_BLOCK_POSITION.top}/>
                </FlexBox>
              }
              {(!isMobile || is744) && isModer && !isPreview && (isSuperPuper || isMediiia) &&
              <FlexBox 
                className='start-game-btn-wrapper' 
                width={'100%'} 
                justify="flex-end">
                  <FlexBox width={is744 ? '100%' : undefined} offsetRightMobile={is744 ? 0 : 16}>
                    <ProjectStartGameButton
                      replayOnly 
                      projectId={project?.projectId} 
                      pollInfo={pollInfo}
                    />
                  </FlexBox>
                </FlexBox>}
              {(!isMobile || is744) && !isSuperPuper && <FlexBox className='queue-btn-wrapper' offsetTop={is744 ? 16 : 0} width={'100%'} justify="flex-end">
                {queueButton}
              </FlexBox>}
              {/* у организаций будет отдельная плашка модерации*/}
              {(!isMobile || is744) && !isCollectionVote && showApprovalDialog && (currentTeam ?
                <OrganizationApprovalDialog project={project} onShouldReload={onShouldReload}/> :
                <ApprovalDialog project={project} reloadProject={reloadProject} onShouldReload={onShouldReload} onBlockedStateChange={onBlockedStateChange}/>)}
            </BlocksWrapper>
            {project.autoTranslateStatus === AutoTranslateStatus.Complete && translatedBlock}
          </ThemeProvider>
        </FlexBox>
      </FlexBox>
    </>
  );
};
